import ScrollModal from "@/components/common/scroll-modal";
import {
  Heading,
  Button,
  TextField,
  Avatar,
  AvatarFallback,
  Icon,
  useToast,
} from "@elaniintc/goes-design-system";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { AxiosError } from "axios";
import SelectField from "@/components/common/select";
import {
  //useGetFlowsByProcessIdAsInfinite,
  useGetFlow,
  useUpdateFlow,
} from "@/queries/flows/flows.api";
import type { FlowsType } from "@/types/entities/flows";
import { z } from "zod";

const editSubFlowSchema = z.object({
  name: z.string().min(1, "El nombre es requerido").max(100),
  parent_flow_id: z.string().min(1, "El padre es requerido"),
});

type EditSubFlowFormData = z.infer<typeof editSubFlowSchema>;

interface EditSubFlowModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  flowId: string;
  //processId: string;
  //currentVersion: number;
  availableParents: FlowsType[];
}

export default function EditSubFlowModal({
  open,
  onClose,
  onSuccess,
  flowId,
  //processId,
  //currentVersion,
  availableParents,
}: EditSubFlowModalProps) {
  const { showToast } = useToast();
  const { data: flowData, isLoading: isLoadingFlow } = useGetFlow(
    flowId,
    false,
  );
  const { mutateAsync, isPending: isUpdating } = useUpdateFlow(flowId);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<EditSubFlowFormData>({
    resolver: zodResolver(editSubFlowSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      name: "",
      parent_flow_id: "",
    },
  });

  const parent_flow_id = watch("parent_flow_id");

  // Pre-cargar datos cuando el flow se carga
  useEffect(() => {
    if (flowData?.data) {
      reset({
        name: flowData.data.name || "",
        parent_flow_id: flowData.data.parent_flow_id || "",
      });
    }
  }, [flowData, reset]);

  const handleCloseModal = () => {
    reset({
      name: "",
      parent_flow_id: "",
    });
    onClose();
  };

  const onSubmit = async (data: EditSubFlowFormData) => {
    try {
      console.log("=== UPDATE FLOW ===");
      console.log("flowId:", flowId);
      console.log("Payload:", {
        name: data.name,
        parent_flow_id: data.parent_flow_id,
      });
      await mutateAsync({
        name: data.name,
        parent_flow_id: data.parent_flow_id,
      });

      console.log("Update exitoso");

      showToast({
        title: "Flujo actualizado",
        description: "El flujo se ha actualizado correctamente.",
        options: {
          type: "success",
        },
      });

      onSuccess?.();
      handleCloseModal();
    } catch (error) {
      console.log("Error:", error);

      const e = error as AxiosError<{
        message: string;
        errors?: { name?: string[] };
      }>;
      console.log("Error response:", e?.response?.data);

      if (e?.response?.data?.errors?.name?.length) {
        setError("name", {
          type: "server",
          message: e.response.data.errors.name[0],
        });
      } else {
        showToast({
          title: "Error",
          description:
            e?.response?.data?.message || "Error al actualizar el flujo",
          options: {
            type: "error",
          },
        });
      }
    }
  };

  // const {
  //   data: flows,
  //   isFetching: isLoadingFlows,
  //   hasNextPage,
  //   fetchNextPage,
  // } = useGetFlowsByProcessIdAsInfinite(processId, {
  //   perPage: 20,
  //   search: "",
  //   order_by: "created_at",
  // });

  // const selectOptions = useMemo(() => {
  //   if (!flows) return [];
  //   return flows.pages
  //     .flatMap((page) => page.data)
  //     .filter((flow) => flow.version === currentVersion)
  //     .filter((flow) => flow.id !== flowId) // Excluir el flujo actual
  //     .map((flow) => {
  //       const isParentFlow = !flow.parent_flow_id;
  //       const avatar = isParentFlow ? (
  //         <Avatar shape="square" size="2xs">
  //           <AvatarFallback color="action" shape="square">
  //             <Icon name="user-circle" type="fas" />
  //           </AvatarFallback>
  //         </Avatar>
  //       ) : (
  //         <Avatar shape="square" size="2xs">
  //           <AvatarFallback color="info" shape="square">
  //             <Icon name="users-rectangle" type="fas" />
  //           </AvatarFallback>
  //         </Avatar>
  //       );
  //       return {
  //         label: `${flow.name} V${flow.version}`,
  //         value: flow.id!,
  //         avatar,
  //       };
  //     });
  // }, [flows, currentVersion, flowId]);

  const selectOptions = useMemo(() => {
    return availableParents
      .filter((flow) => flow.id !== flowId) // Excluir el flujo actual
      .map((flow) => {
        const isParentFlow = !flow.parent_flow_id;
        const avatar = isParentFlow ? (
          <Avatar shape="square" size="2xs">
            <AvatarFallback color="action" shape="square">
              <Icon name="user-circle" type="fas" />
            </AvatarFallback>
          </Avatar>
        ) : (
          <Avatar shape="square" size="2xs">
            <AvatarFallback color="info" shape="square">
              <Icon name="users-rectangle" type="fas" />
            </AvatarFallback>
          </Avatar>
        );
        return {
          label: flow.name,
          value: flow.id!,
          avatar,
        };
      });
  }, [availableParents, flowId]);

  return (
    <ScrollModal
      isOpen={open && !!flowData}
      onClose={handleCloseModal}
      title="Editar subflujo"
      description="Modifica el nombre y el padre de este flujo."
      srOnlyDescription={false}
      modalContentClassName="w-full max-w-[520px] max-h-[680px] flex flex-col pb-6"
    >
      <div className="flex py-8 justify-center flex-col items-center">
        <Heading variant="h2" className="!mb-0">
          Editar subflujo
        </Heading>
        <p className="text-center text-body font-normal leading-3xl">
          Modifica la configuración de tu subflujo.
        </p>
      </div>
      <div className="flex flex-col">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col flex-1 min-h-0 px-4 sm:px-8 gap-8"
        >
          <TextField
            label="Nombre del flujo"
            required
            error={errors.name?.message}
            placeholder="Escribe el nombre del flujo"
            {...register("name")}
          />
          <SelectField
            label="Padre del flujo"
            name="parent-flow"
            onChange={(parent_id) => {
              setValue("parent_flow_id", parent_id, { shouldValidate: true });
            }}
            options={selectOptions}
            placeholder="Selecciona un flujo..."
            value={parent_flow_id}
            //isLoading={isLoadingFlows || isLoadingFlow}
            isLoading={isLoadingFlow}
            // isDisabled={
            //   (isLoadingFlows && selectOptions.length === 0) || isLoadingFlow
            // }
            isDisabled={isLoadingFlow}
            required
            //hasMore={hasNextPage}
            //onLoadMore={fetchNextPage}
            error={errors.parent_flow_id?.message || ""}
          />
          <div className="w-full grid grid-cols-1 tablet:grid-cols-2 py-3 gap-3">
            <Button
              variant="tertiary"
              size="sm"
              type="button"
              disabled={isUpdating}
              onClick={handleCloseModal}
              className="min-w-30 cursor-pointer"
            >
              Cancelar
            </Button>
            <Button
              variant="default"
              size="sm"
              type="submit"
              disabled={!isValid || isUpdating}
              isLoading={isUpdating}
              className="min-w-35 cursor-pointer disabled:bg-neutral-object-accent disabled:text-on-disabled disabled:border-none disabled:shadow-none"
            >
              Guardar cambios
            </Button>
          </div>
        </form>
      </div>
    </ScrollModal>
  );
}
